import React, { useEffect } from "react";
import DetalleCard from "~business/Cards/DetalleCard";
import { useDispatch, useSelector } from "react-redux";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { formasPagoOptions, formasPagoFilter } from "../constants/formasPago";

import ItemInputCard from "~business/Cards/ItemInputCard";
import ItemDominioCard from "~business/Cards/ItemDominioCard";
import ItemCurrencyCard from "~business/Cards/ItemCurrencyCard";
import ItemSelectorCard from "~business/Cards/ItemSelectorCard";
import ItemDateCard from "~business/Cards/ItemDateCard";
import { FieldChangeEvent } from "~business/Cards/types";
import { FechaFormulario, GetAseguradoResponse } from "~api/types";
import { CampoFormulario, RamaSeleccionada } from "../../types";
import {
  vigenciasPolizasMultiramaFilter,
  vigenciasPolizasMultiramaOptions,
} from "../constants/vigencias";
import { cuotasFilter, cuotasOptions } from "../constants/cuotas";
import {
  setNumeroPoliza,
  setDominio,
  setVigencia,
  setVigenciaDesde,
  setPremio,
  setSubmitting,
  setCantidadCuotas,
  setFormaPago,
} from "../actions";

/**
 * `ramaSelected` se esparce en el slice: de ahí sale `tieneDominio`, que decide
 * si la póliza pide dominio.
 */
interface SliceDatosPoliza extends Partial<RamaSeleccionada> {
  numeroPoliza: CampoFormulario<string | null>;
  nombreAsegurado: CampoFormulario<string | null>;
  apellidoAsegurado: CampoFormulario<string | null>;
  dni: CampoFormulario<string | null>;
  dominio: CampoFormulario<string | null>;
  premio: CampoFormulario<string | number | null>;
  cuotas: CampoFormulario<number>;
  vigencia: CampoFormulario<number>;
  vigenciaDesde: CampoFormulario<Date>;
  vigenciaHasta: CampoFormulario<FechaFormulario>;
  formaPago: CampoFormulario<string>;
  aseguradoFromDocumento: GetAseguradoResponse | null;
}

interface DatosPolizaCardProps {
  isLoading: boolean;
}

const useReduxSelector = () =>
  useSelector<any, SliceDatosPoliza>((state) => {
    const ramaSelected = state.altaPolizaMultiramaReducer.ramaSelected
      ? state.altaPolizaMultiramaReducer.ramaSelected
      : {};

    return {
      numeroPoliza: state.altaPolizaMultiramaReducer.numeroPolizaSelected,
      nombreAsegurado: state.altaPolizaMultiramaReducer.aseguradoNombreSelected,
      apellidoAsegurado:
        state.altaPolizaMultiramaReducer.aseguradoApellidoSelected,
      dni: state.altaPolizaMultiramaReducer.aseguradoNumeroDocumentoSelected,
      dominio: state.altaPolizaMultiramaReducer.dominioSelected,
      premio: state.altaPolizaMultiramaReducer.premioSelected,
      cuotas: state.altaPolizaMultiramaReducer.cuotasSelected,
      vigencia: state.altaPolizaMultiramaReducer.vigenciaSelected,
      vigenciaDesde: state.altaPolizaMultiramaReducer.vigenciaDesdeSelected,
      vigenciaHasta: state.altaPolizaMultiramaReducer.vigenciaHastaSelected,
      formaPago: state.altaPolizaMultiramaReducer.formaPagoSelected,
      aseguradoFromDocumento: state.altaPolizaMultiramaReducer.asegurado,
      ...ramaSelected,
    };
  });

const title = "Datos para la Póliza";

const DatosPolizaCard: React.FC<DatosPolizaCardProps> = ({ isLoading }) => {
  const dispatch = useDispatch();

  const {
    numeroPoliza,
    dominio,
    cuotas,
    vigencia,
    vigenciaDesde,
    vigenciaHasta,
    premio,
    formaPago,
    tieneDominio,
  } = useReduxSelector();

  useEffect(() => {
    dispatch(setVigenciaDesde(vigenciaDesde));
  }, [dispatch, vigenciaDesde]);

  let timer: ReturnType<typeof setTimeout> | undefined;

  const handleBlur = () => {
    dispatch(setSubmitting(false));
  };

  const handleChangeNumeroPoliza = (
    nroPoliza: FieldChangeEvent<string | null>
  ) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setNumeroPoliza(nroPoliza));
    }, 200);
  };

  const handleChangeDominio = (dominio: FieldChangeEvent<string>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setDominio(dominio));
    }, 200);
  };

  const handleChangePremio = (premio: FieldChangeEvent<string | null>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setPremio(premio));
    }, 200);
  };

  const handleChangeCantidadCuotas = (
    cantidadCuotas: CampoFormulario<number>
  ) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setCantidadCuotas(cantidadCuotas));
    }, 200);
  };

  const handleChangeFormaPago = (formaPago: CampoFormulario<string>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setFormaPago(formaPago));
    }, 200);
  };

  const handleChangeVigencia = (vigencia: CampoFormulario<number>) => {
    dispatch(setVigencia(vigencia));
  };

  const handleChangeVigenciaDesde = (
    vigenciaDesde: FieldChangeEvent<string | Date | null>
  ) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      // El picker también puede emitir null, con el campo vacío. El reducer lo
      // trata como Date igual que antes de la migración: se preserva el
      // comportamiento previo, no es que el caso no exista.
      dispatch(setVigenciaDesde(vigenciaDesde as CampoFormulario<Date>));
    }, 200);
  };

  return (
    <>
      <DetalleCard title={title} IconHeader={VerifiedUserIcon}>
        <ItemInputCard
          id={"nroPoliza"}
          isLoading={isLoading}
          label={"Número de Póliza"}
          value={numeroPoliza.value}
          onChange={handleChangeNumeroPoliza}
          onBlur={handleBlur}
          focus={numeroPoliza.focus}
          required
        />

        {tieneDominio && (
          <ItemDominioCard
            id={"dominio"}
            isLoading={isLoading}
            label={"Dominio"}
            value={dominio.value}
            onChange={handleChangeDominio}
            onBlur={handleBlur}
            focus={dominio.focus}
            required
          />
        )}
        <ItemSelectorCard<number>
          id={"cantidadCuotas"}
          label={"Cantidad de Cuotas"}
          isLoading={isLoading}
          value={cuotas.value}
          onChange={handleChangeCantidadCuotas}
          options={cuotasFilter}
          valueOptions={cuotasOptions}
          variant="big"
          required
        />
        <ItemSelectorCard<string>
          id={"formaPago"}
          label={"Forma de Pago"}
          isLoading={isLoading}
          value={formaPago.value}
          onChange={handleChangeFormaPago}
          options={formasPagoFilter}
          valueOptions={formasPagoOptions}
          variant="big"
          required
        />
        <ItemSelectorCard<number>
          id={"vigencia"}
          isLoading={isLoading}
          label={"Vigencia"}
          value={vigencia.value}
          onChange={handleChangeVigencia}
          options={vigenciasPolizasMultiramaFilter}
          valueOptions={vigenciasPolizasMultiramaOptions}
          variant="big"
          required
        />
        <ItemDateCard
          id={"vigenciaDesde"}
          label={"Vigencia Desde"}
          isLoading={isLoading}
          value={vigenciaDesde.value}
          onChange={handleChangeVigenciaDesde}
          onBlur={handleBlur}
        />
        <ItemDateCard
          id="vigenciaHasta"
          label="Vigencia Hasta"
          isLoading={isLoading}
          // El reducer deja acá un Date o un moment según cómo se recalculó.
          value={vigenciaHasta.value as Date}
          readOnly
        />
        <ItemCurrencyCard
          id={"premio"}
          isLoading={isLoading}
          label={"Premio Mensual"}
          value={premio.value as string | null}
          onChange={handleChangePremio}
          onBlur={handleBlur}
          focus={premio.focus}
          required
        />
      </DetalleCard>
    </>
  );
};

export default DatosPolizaCard;
